"use client";

/**
 * Notification state for the header bell.
 *
 * Three ways of learning that something changed, in decreasing order of how
 * often they carry the news:
 *
 *  1. **SSE** (`/v1/notifications/stream`) — an open connection per tab,
 *     carrying counts. Covers *every* signed-in tab, including the majority
 *     whose user never granted notification permission.
 *  2. **A service-worker message** — fires when a Web Push lands. Only for
 *     users who granted permission, but it is the one that also works when the
 *     app was closed and has just been reopened.
 *  3. **Polling** — a slow backstop, and the only thing that works if the SSE
 *     connection is being eaten by a proxy or the browser has capped its
 *     connections. Deliberately lazy (60s, visible tabs only): if it is doing
 *     the work, something above it is broken.
 *
 * They are not redundant so much as differently blind, and all three converge
 * on the same `counts` state, so a duplicate is a no-op rather than a bug.
 */

import { useCallback, useEffect, useRef, useState } from "react";

export interface NotificationAction {
  label: string;
  href: string;
}

export interface Notification {
  id: string;
  kind: string;
  title: string;
  body: string | null;
  level: "info" | "action" | "urgent";
  action: NotificationAction | null;
  producedBy: string;
  createdAt: string;
  expiresAt: string | null;
  readAt: string | null;
  seenAt: string | null;
}

export interface NotificationCounts {
  unread: number;
  unseen: number;
}

const apiUrl =
  process.env.NEXT_PUBLIC_NOTIFICATIONS_API_URL || "https://api.notifications.gaz.codes";

/** Backstop poll interval. See the note above on why it is this slow. */
const POLL_MS = 60_000;

export interface UseNotificationsOptions {
  /**
   * Surfaces this view can render inline, e.g. `["docs:abc123"]`.
   *
   * The server signals that something arrived on a surface the user was
   * looking at; this says which of those *this view* can actually show. A
   * signal for anything else is ignored — the notification still lands in the
   * bell, which is the whole point of the record being written regardless.
   */
  inlineSurfaces?: string[];
  /**
   * Called with the notification behind an inline signal, once fetched.
   *
   * **The app decides what to do with it.** The server forwards facts — the
   * notification, and that the user was present on its surface; only the app
   * knows whether the current view can render anything, so presentation is its
   * call. Wire this to an activity store, a toast, an inline highlight, or
   * ignore it.
   */
  onInline?: (notification: Notification) => void;
}

export interface UseNotificationsReturn {
  counts: NotificationCounts;
  notifications: Notification[];
  loading: boolean;
  /** Fetch the full list — called when the panel opens, not on mount. */
  load: () => Promise<void>;
  /** Clear the "something new" dot without marking anything read. */
  markSeen: () => Promise<void>;
  markRead: (ids?: string[]) => Promise<void>;
  dismiss: (id: string) => Promise<void>;
}

/**
 * @param enabled Pass `false` when nobody is signed in. Every request here is
 *   authenticated, so running this for an anonymous visitor produces a stream
 *   of 401s and an SSE connection that reconnects forever.
 */
export function useNotifications(
  enabled: boolean,
  options: UseNotificationsOptions = {},
): UseNotificationsReturn {
  const [counts, setCounts] = useState<NotificationCounts>({ unread: 0, unseen: 0 });
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);

  // Held in a ref so the SSE effect does not re-run (and reconnect) every time
  // the counts change — which is every time it delivers an event.
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  // Held in refs for the same reason as `enabled`: a caller passing an inline
  // array or handler literal would otherwise change identity every render and
  // tear down the SSE connection with it.
  const inlineSurfacesRef = useRef(options.inlineSurfaces);
  inlineSurfacesRef.current = options.inlineSurfaces;
  const onInlineRef = useRef(options.onInline);
  onInlineRef.current = options.onInline;

  /**
   * Notification ids this tab has already handed to the app.
   *
   * Bounded: an EventSource can reconnect indefinitely on a long-lived page, so
   * an unbounded set here would be a slow leak. Oldest entries are dropped
   * once it grows past the cap — replaying a signal that old is not a case
   * worth holding memory for.
   */
  const shownInlineRef = useRef<Set<string>>(new Set());

  const refreshCounts = useCallback(async () => {
    if (!enabledRef.current) return;
    try {
      const res = await fetch(`${apiUrl}/v1/notifications/counts`, {
        credentials: "include",
      });
      if (res.ok) setCounts((await res.json()) as NotificationCounts);
    } catch {
      // A failed count check is not worth surfacing: the badge keeps its last
      // value, and the next event or poll corrects it.
    }
  }, []);

  const load = useCallback(async () => {
    if (!enabledRef.current) return;
    setLoading(true);
    try {
      const res = await fetch(`${apiUrl}/v1/notifications?limit=50`, {
        credentials: "include",
      });
      if (res.ok) {
        const data = (await res.json()) as {
          notifications: Notification[];
          counts: NotificationCounts;
        };
        setNotifications(data.notifications);
        setCounts(data.counts);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // ── 1. SSE ────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!enabled || typeof window === "undefined" || !("EventSource" in window)) return;

    // `withCredentials` is what carries the auth cookie cross-subdomain; without
    // it the stream 401s and EventSource retries forever, which looks like a
    // working reconnect loop and is not.
    const source = new EventSource(`${apiUrl}/v1/notifications/stream`, {
      withCredentials: true,
    });

    source.addEventListener("counts", (event) => {
      try {
        setCounts(JSON.parse((event as MessageEvent).data) as NotificationCounts);
      } catch {
        // Malformed frame; the next one will be fine.
      }
    });

    /**
     * Something arrived on a surface the user was looking at.
     *
     * The event carries only the surface — no content — so the store stays the
     * single source by construction. If this view can render that surface, fetch
     * the newest item and hand it to the app.
     */
    source.addEventListener("inline", (event) => {
      let signal: { surface: string; id: string };
      try {
        signal = JSON.parse((event as MessageEvent).data) as typeof signal;
      } catch {
        return;
      }

      const handler = onInlineRef.current;
      if (!handler || !signal.id) return;
      if (!inlineSurfacesRef.current?.includes(signal.surface)) return;

      // Has THIS tab already rendered it? Per-tab state, so the server cannot
      // answer it — its `seen_at` is per-user and set when the panel opens, not
      // when a client fetches, because fetching is not seeing. Without this an
      // EventSource reconnect could replay a signal and toast the same item
      // twice.
      if (shownInlineRef.current.has(signal.id)) return;
      shownInlineRef.current.add(signal.id);
      if (shownInlineRef.current.size > 200) {
        // Sets iterate in insertion order, so the first key is the oldest.
        const oldest = shownInlineRef.current.values().next().value;
        if (oldest) shownInlineRef.current.delete(oldest);
      }

      // Fetch the NAMED record, not "the newest". Those are different
      // notifications whenever two arrive close together or a dedupe refresh
      // reorders the list — the bug this id exists to remove.
      void fetch(`${apiUrl}/v1/notifications/item/${encodeURIComponent(signal.id)}`, {
        credentials: "include",
      })
        .then((res) => (res.ok ? res.json() : null))
        .then((data: { notification?: Notification } | null) => {
          if (data?.notification) handler(data.notification);
        })
        .catch(() => {
          // A missed inline render costs a toast, never the notification — it
          // is in the bell either way. Deliberately NOT removed from the shown
          // set on failure: a retry would most likely fail the same way, and a
          // duplicate toast is worse than a missing one.
        });
    });

    // No onerror handler that closes the source: EventSource reconnects on its
    // own with backoff, and closing it here would turn a transient blip into a
    // permanently dead stream for the life of the page.

    return () => source.close();
  }, [enabled]);

  // ── 2. Service-worker push message ────────────────────────────────────────
  useEffect(() => {
    if (!enabled || typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;

    const listener = (event: MessageEvent) => {
      if ((event.data as { type?: string } | undefined)?.type === "notification") {
        void refreshCounts();
      }
    };
    navigator.serviceWorker.addEventListener("message", listener);
    return () => navigator.serviceWorker.removeEventListener("message", listener);
  }, [enabled, refreshCounts]);

  // ── 3. Polling backstop ───────────────────────────────────────────────────
  useEffect(() => {
    if (!enabled) return;

    void refreshCounts();

    const tick = () => {
      // Never poll a hidden tab. A user with a dozen apps open in background
      // tabs would otherwise generate a dozen requests a minute for a badge
      // nobody is looking at.
      if (document.visibilityState === "visible") void refreshCounts();
    };

    const timer = setInterval(tick, POLL_MS);
    // Returning to a tab is the moment a stale badge is most visible, and the
    // moment an SSE connection dropped while backgrounded gets noticed.
    document.addEventListener("visibilitychange", tick);

    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", tick);
    };
  }, [enabled, refreshCounts]);

  const markSeen = useCallback(async () => {
    if (!enabledRef.current) return;
    // Optimistic: the dot should vanish on click, not after a round trip.
    setCounts((c) => ({ ...c, unseen: 0 }));
    try {
      const res = await fetch(`${apiUrl}/v1/notifications/seen`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: "{}",
      });
      if (res.ok) {
        const data = (await res.json()) as { counts: NotificationCounts };
        setCounts(data.counts);
      }
    } catch {
      // The optimistic update stands; the next event corrects it if wrong.
    }
  }, []);

  const markRead = useCallback(async (ids?: string[]) => {
    if (!enabledRef.current) return;
    try {
      const res = await fetch(`${apiUrl}/v1/notifications/read`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ids }),
      });
      if (res.ok) {
        const data = (await res.json()) as { counts: NotificationCounts };
        setCounts(data.counts);
        const now = new Date().toISOString();
        setNotifications((list) =>
          list.map((n) =>
            !ids || ids.includes(n.id) ? { ...n, readAt: n.readAt ?? now } : n,
          ),
        );
      }
    } catch {
      // Leave local state alone; a reload shows the truth.
    }
  }, []);

  const dismiss = useCallback(async (id: string) => {
    if (!enabledRef.current) return;
    setNotifications((list) => list.filter((n) => n.id !== id));
    try {
      const res = await fetch(`${apiUrl}/v1/notifications/${id}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (res.ok) {
        const data = (await res.json()) as { counts: NotificationCounts };
        setCounts(data.counts);
      }
    } catch {
      // Removed locally regardless — re-appearing on the next load is a better
      // failure than a row that refuses to go away.
    }
  }, []);

  return { counts, notifications, loading, load, markSeen, markRead, dismiss };
}
